import { useState, useEffect } from "react";
import { Search, Plus, Edit2, Trash2, Loader2, X, CheckCircle, AlertCircle, ChevronDown, ChevronUp, Users } from "lucide-react";
import { adminService, Client, ClientUser, ClientFormData } from "@/services/adminService";

export function ClientManagement() {
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [expandedClient, setExpandedClient] = useState<number | null>(null);
  const [clientUsers, setClientUsers] = useState<ClientUser[]>([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<ClientUser | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const [formData, setFormData] = useState<ClientFormData>({
    client_type: "pj",
    company_name: "",
    trading_name: "",
    cnpj: "",
    cpf_holder: "",
    email: "",
    phone: "",
    address_street: "",
    address_number: "",
    address_complement: "",
    address_neighborhood: "",
    address_city: "",
    address_state: "",
    address_zipcode: "",
    segment: "",
    status: "active",
    priority: "medium",
    notes: "",
  });

  const [userForm, setUserForm] = useState({
    full_name: "",
    email: "",
    password: "",
    phone: "",
    role_id: 6, // client_user
  });

  useEffect(() => {
    loadClients();
  }, [searchTerm]);

  const loadClients = async () => {
    setIsLoading(true);
    try {
      const result = await adminService.listClients(searchTerm);
      setClients(result.data);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
      setMessage({ type: "error", text: "Erro ao carregar clientes" });
    } finally {
      setIsLoading(false);
    }
  };

  const loadClientUsers = async (clientId: number) => {
    try {
      const users = await adminService.listClientUsers(clientId);
      setClientUsers(users);
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
    }
  };

  const toggleExpand = async (clientId: number) => {
    if (expandedClient === clientId) {
      setExpandedClient(null);
    } else {
      setExpandedClient(clientId);
      await loadClientUsers(clientId);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await adminService.updateClient(editingClient.id, formData);
        setMessage({ type: "success", text: "Cliente atualizado com sucesso!" });
      } else {
        await adminService.createClient(formData);
        setMessage({ type: "success", text: "Cliente criado com sucesso!" });
      }
      closeModal();
      loadClients();
    } catch (error: any) {
      setMessage({ type: "error", text: error.message || "Erro ao salvar cliente" });
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      client_type: client.client_type,
      company_name: client.company_name || "",
      trading_name: client.trading_name || "",
      cnpj: client.cnpj || "",
      cpf_holder: client.cpf_holder || "",
      email: client.email,
      phone: client.phone || "",
      address_city: client.address_city || "",
      address_state: client.address_state || "",
      status: client.status,
      priority: client.priority,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Deseja desativar este cliente?")) return;
    try {
      await adminService.deleteClient(id);
      setMessage({ type: "success", text: "Cliente desativado!" });
      loadClients();
    } catch {
      setMessage({ type: "error", text: "Erro ao desativar cliente" });
    }
  };

  const openNewModal = () => {
    setEditingClient(null);
    setFormData({
      client_type: "pj",
      company_name: "",
      trading_name: "",
      cnpj: "",
      cpf_holder: "",
      email: "",
      phone: "",
      address_street: "",
      address_number: "",
      address_complement: "",
      address_neighborhood: "",
      address_city: "",
      address_state: "",
      address_zipcode: "",
      segment: "",
      status: "active",
      priority: "medium",
      notes: "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingClient(null);
  };

  // User management
  const openNewUserModal = (clientId: number) => {
    setEditingUser(null);
    setSelectedClientId(clientId);
    setUserForm({ full_name: "", email: "", password: "", phone: "", role_id: 6 });
    setShowUserModal(true);
  };

  const handleEditUser = (user: ClientUser, clientId: number) => {
    setEditingUser(user);
    setSelectedClientId(clientId);
    setUserForm({
      full_name: user.full_name,
      email: user.email,
      password: "",
      phone: user.phone || "",
      role_id: user.role_id,
    });
    setShowUserModal(true);
  };

  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClientId) return;
    try {
      if (editingUser) {
        await adminService.updateClientUser(editingUser.id, userForm);
        setMessage({ type: "success", text: "Usuário atualizado com sucesso!" });
      } else {
        await adminService.createClientUser(selectedClientId, userForm as any);
        setMessage({ type: "success", text: "Usuário criado com sucesso!" });
      }
      closeUserModal();
      loadClientUsers(selectedClientId);
    } catch (error: any) {
      setMessage({ type: "error", text: error.message || "Erro ao salvar usuário" });
    }
  };

  const handleDeleteUser = async (id: number) => {
    if (!confirm("Deseja desativar este usuário?")) return;
    try {
      await adminService.deleteClientUser(id);
      setMessage({ type: "success", text: "Usuário desativado!" });
      if (expandedClient) loadClientUsers(expandedClient);
    } catch {
      setMessage({ type: "error", text: "Erro ao desativar usuário" });
    }
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setEditingUser(null);
  };

  const clientRoles = [
    { value: 5, label: "Administrador do Cliente" },
    { value: 6, label: "Usuário do Cliente" },
  ];

  const statusColors: Record<string, string> = {
    active: "bg-green-100 text-green-800",
    inactive: "bg-gray-100 text-gray-800",
    suspended: "bg-red-100 text-red-800",
    lead: "bg-blue-100 text-blue-800",
    prospect: "bg-yellow-100 text-yellow-800",
  };

  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
          {message.type === "success" ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar clientes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder:text-gray-400"
          />
        </div>
        <button onClick={openNewModal} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <Plus className="w-4 h-4" /> Novo Cliente
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-12 text-gray-500">Nenhum cliente encontrado</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {clients.map((client) => (
              <div key={client.id}>
                <div className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {client.client_type === 'pj' ? client.company_name : client.cpf_holder}
                    </p>
                    <p className="text-sm text-gray-500">
                      {client.email} • {client.client_type === 'pj' ? client.cnpj : 'PF'}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-500">{client.users_count || 0} usuários</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${statusColors[client.status] || "bg-gray-100 text-gray-800"}`}>
                      {client.status === 'active' ? 'Ativo' : client.status === 'inactive' ? 'Inativo' : client.status}
                    </span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => toggleExpand(client.id)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                        {expandedClient === client.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleEdit(client)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(client.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded: Users */}
                {expandedClient === client.id && (
                  <div className="bg-gray-50 p-4 border-t">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-700 flex items-center gap-2">
                        <Users className="w-4 h-4" /> Usuários de Acesso
                      </h4>
                      <button onClick={() => openNewUserModal(client.id)} className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                        <Plus className="w-4 h-4" /> Novo Usuário
                      </button>
                    </div>
                    {clientUsers.length === 0 ? (
                      <p className="text-sm text-gray-500">Nenhum usuário cadastrado para este cliente.</p>
                    ) : (
                      <div className="space-y-2">
                        {clientUsers.map((user) => (
                          <div key={user.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                            <div>
                              <p className="font-medium text-gray-900">{user.full_name}</p>
                              <p className="text-sm text-gray-500">{user.email} • {user.role_name || 'Usuário'}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded-full text-xs ${user.status === 'active' ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                                {user.status === 'active' ? 'Ativo' : 'Inativo'}
                              </span>
                              <button onClick={() => handleEditUser(user, client.id)} className="p-1 text-blue-600 hover:bg-blue-50 rounded">
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleDeleteUser(user.id)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Cliente */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-2xl p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{editingClient ? "Editar Cliente" : "Novo Cliente"}</h3>
              <button onClick={closeModal}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                  <select
                    value={formData.client_type}
                    onChange={(e) => setFormData(p => ({ ...p, client_type: e.target.value as 'pf' | 'pj' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900"
                  >
                    <option value="pj">Pessoa Jurídica</option>
                    <option value="pf">Pessoa Física</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(p => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900"
                  >
                    <option value="lead">Lead</option>
                    <option value="prospect">Prospect</option>
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                    <option value="suspended">Suspenso</option>
                  </select>
                </div>
              </div>

              {formData.client_type === 'pj' ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Razão Social *</label>
                      <input type="text" value={formData.company_name} onChange={(e) => setFormData(p => ({ ...p, company_name: e.target.value }))} required className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nome Fantasia</label>
                      <input type="text" value={formData.trading_name} onChange={(e) => setFormData(p => ({ ...p, trading_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CNPJ *</label>
                      <input type="text" value={formData.cnpj} onChange={(e) => setFormData(p => ({ ...p, cnpj: e.target.value }))} required className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                    </div>
                  </div>
                </>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CPF *</label>
                  <input type="text" value={formData.cpf_holder} onChange={(e) => setFormData(p => ({ ...p, cpf_holder: e.target.value }))} required className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} required className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <input type="text" value={formData.phone} onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
              </div>

              <h4 className="font-medium text-gray-700 pt-2 border-t">Endereço</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                  <input type="text" value={formData.address_zipcode} onChange={(e) => setFormData(p => ({ ...p, address_zipcode: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logradouro</label>
                  <input type="text" value={formData.address_street} onChange={(e) => setFormData(p => ({ ...p, address_street: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Número</label>
                  <input type="text" value={formData.address_number} onChange={(e) => setFormData(p => ({ ...p, address_number: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bairro</label>
                  <input type="text" value={formData.address_neighborhood} onChange={(e) => setFormData(p => ({ ...p, address_neighborhood: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                  <input type="text" value={formData.address_city} onChange={(e) => setFormData(p => ({ ...p, address_city: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <input type="text" value={formData.address_state} onChange={(e) => setFormData(p => ({ ...p, address_state: e.target.value }))} maxLength={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                <textarea value={formData.notes} onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button type="button" onClick={closeModal} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700">Cancelar</button>
                <button type="submit" className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Usuário */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{editingUser ? "Editar Usuário" : "Novo Usuário"}</h3>
              <button onClick={closeUserModal}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleUserSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo *</label>
                <input type="text" value={userForm.full_name} onChange={(e) => setUserForm(p => ({ ...p, full_name: e.target.value }))} required className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" value={userForm.email} onChange={(e) => setUserForm(p => ({ ...p, email: e.target.value }))} required className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Senha {editingUser ? "(deixe vazio para manter)" : "*"}</label>
                <input type="password" value={userForm.password} onChange={(e) => setUserForm(p => ({ ...p, password: e.target.value }))} required={!editingUser} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                <input type="text" value={userForm.phone} onChange={(e) => setUserForm(p => ({ ...p, phone: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Perfil</label>
                <select value={userForm.role_id} onChange={(e) => setUserForm(p => ({ ...p, role_id: parseInt(e.target.value) }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900">
                  {clientRoles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={closeUserModal} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700">Cancelar</button>
                <button type="submit" className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
